import React from 'react';
import type { Model, PageModel } from 'survey-core';
import { useTranslation } from '@/hooks/useTranslation';

interface SurveyStepperProps {
  survey: Model;
  /** Index into `survey.pages` (not visible pages) of the current page. */
  currentPageIndex: number;
  /** Fallback label when a page has no authored title. */
  fallbackLabel?: (visibleIndex: number) => string;
}

/**
 * Numbered step indicator from the approved web design ("Screen 01 - About
 * You (Web)"): one circle per VISIBLE page, 2px connectors between them, the
 * page title under each circle; the current step is filled and bold,
 * completed steps show a check, upcoming steps are outlined.
 *
 * Exposed to assistive tech as an ordered list with `aria-current="step"`
 * on the current item; the visible number is decorative because the item's
 * text already carries "Step n of m".
 */
const SurveyStepper: React.FC<SurveyStepperProps> = ({ survey, currentPageIndex, fallbackLabel }) => {
  const { t } = useTranslation();
  const visiblePages: PageModel[] = survey.visiblePages;
  if (visiblePages.length < 2) return null;

  return (
    <nav className="survey-stepper" aria-label={t('surveyProgress')}>
      <ol className="survey-stepper__list">
        {visiblePages.map((page, visibleIndex) => {
          const pageIndex = survey.pages.indexOf(page);
          const state = pageIndex === currentPageIndex ? 'current' : pageIndex < currentPageIndex ? 'done' : 'todo';
          const label = page.title || fallbackLabel?.(visibleIndex) || `${visibleIndex + 1}`;
          return (
            <li
              key={page.name || visibleIndex}
              className="survey-stepper__step"
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <div className="survey-stepper__track">
                <span className="survey-stepper__line" data-edge={visibleIndex === 0 ? 'start' : undefined} />
                <span className="survey-stepper__circle" aria-hidden="true">
                  {state === 'done' ? '✓' : visibleIndex + 1}
                </span>
                <span
                  className="survey-stepper__line"
                  data-edge={visibleIndex === visiblePages.length - 1 ? 'end' : undefined}
                />
              </div>
              <span className="survey-stepper__label">
                <span className="sr-only">
                  {t('pageXofY')
                    .replace('{0}', String(visibleIndex + 1))
                    .replace('{1}', String(visiblePages.length))}{' '}
                </span>
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default SurveyStepper;
